import { ReactNode, useMemo, useEffect } from 'react';
import { Link } from 'react-router';

import { Image, ImgPath } from '@/shared/ui';

import { ErrorType } from '@/components/ErrorBoundary/ErrorBoundary';

import { Timer } from '../../components/Timer';
import { ROUTES } from '../../router/config';

import './ErrorPage.scss';

interface ErrorPageProps {
  errorType: ErrorType;
  timeout?: number;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ errorType, timeout = 0 }) => {
  const errorData = useMemo<{ img?: ImgPath; title: ReactNode; description: ReactNode }>(() => {
    switch (errorType) {
      case 404:
        return {
          img: '/images/404.svg',
          title: 'Ой, щось пішло не так...',
          description: 'Такої сторінки не існує.',
        };

      case 500:
        return {
          img: '/images/500.svg',
          title: 'Шановний клієнт!',
          description: (
            <>
              <div>Наразі на сайті проводяться технічні роботи.</div>
              <div>Спробуйте зайти трохи пізніше.</div>
              <div>Дякуємо за розуміння.</div>
            </>
          ),
        };

      case 'antiFlood':
        return {
          title: (
            <div>
              <div className="timer-container">
                <Timer seconds={timeout} />
              </div>
              <h1>Шановний клієнт!</h1>
            </div>
          ),
          description: (
            <>
              <div>На жаль, сталася помилка. Повторити спробу Ви зможете через {timeout} секунд.</div>
              <div>Дякуємо за розуміння.</div>
            </>
          ),
        };

      case 'typeError':
      default:
        return {
          title: 'Ой, щось пішло не так...',
          description: 'Вас буде перенаправлено на головну сторінку автоматично за декілька секунд',
        };
    }
  }, [errorType, timeout]);

  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex';
    document.head.appendChild(metaRobots);
    return (): void => metaRobots.remove();
  }, []);

  return (
    <div className={`error-page error-page--${errorType}`}>
      <div className="error-page__header">
        <span className="error-page__header__logo">
          <Image src="/images/Logo_grey.svg" alt="Logo" />
        </span>
        <div className="error-page__header__title">Єдине вікно</div>
      </div>

      <div className="error-page__body">
        {errorData.img && (
          <span className="error-page__body__img">
            <Image src={errorData.img} alt={`Error - ${errorType}`} />
          </span>
        )}

        <div className="error-page__body__title">{errorData.title}</div>
        <div className="error-page__body__description">{errorData.description}</div>

        {errorType === 404 && (
          <div>
            <Link className="button" to={ROUTES.HOME}>
              На головну
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorPage;
